import { Theme } from '@mui/material/styles'
import { FieldSectionContentType, pickersDayClasses } from '@mui/x-date-pickers'
import { pickersLayoutClasses } from '@mui/x-date-pickers/PickersLayout'

import { popperGap } from './Popover'

// ----------------------------------------------------------------------

/**
 * What an empty month section of a date field says. MUI writes a month shown
 * as a word as `MMMM`, a format token nobody outside a format string reads, so
 * an empty `day` field read `DD MMMM YYYY` (2026-10-19). A month shown as a
 * word says `Month`; one shown as a number keeps `MM`.
 */
export const fieldMonthPlaceholder = ({
  contentType,
}: {
  contentType: FieldSectionContentType
}) => (contentType === 'letter' ? 'Month' : 'MM')

export default function DatePicker(theme: Theme) {
  return {
    // Every picker nests a provider of its own inside the theme, so a default
    // here reaches every date field, the app's root provider being outside it.
    MuiLocalizationProvider: {
      defaultProps: {
        localeText: { fieldMonthPlaceholder },
      },
    },
    // A desktop picker's calendar stands off its field as a menu does. The
    // class is written out: v6 exports `pickersPopperClasses` only from its
    // internals.
    MuiPickersPopper: {
      styleOverrides: {
        root: popperGap(theme, 'MuiPickersPopper-paper'),
      },
    },
    // The Cancel and OK under a phone's calendar are a `DialogActions`, and a
    // dialog foot carries no top inset of its own, because the block above it
    // ends on one. A calendar does not, so this bar keeps the 24 it had.
    MuiPickersLayout: {
      styleOverrides: {
        root: {
          [`& .${pickersLayoutClasses.actionBar}`]: {
            paddingTop: theme.spacing(3),
          },
        },
      },
    },
    MuiYearPicker: {
      styleOverrides: {
        root: {
          button: {
            '&.Mui-selected': {
              'backgroundColor': theme.palette.secondary.main,
              '&:hover, &:focus': {
                backgroundColor: theme.palette.secondary.dark,
              },
            },
          },
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          [`&.${pickersDayClasses.selected}`]: {
            backgroundColor: theme.palette.secondary.main,
          },
          [`&:hover.${pickersDayClasses.selected}`]: {
            backgroundColor: theme.palette.secondary.dark,
          },
          [`&:focus.${pickersDayClasses.selected}`]: {
            backgroundColor: theme.palette.secondary.dark,
          },
        },
      },
    },
  }
}
